import dayjs, { type Dayjs } from 'dayjs';
import minMaxPlugin from 'dayjs/plugin/minMax';
import { parseDateLimit } from '@folio/utils/date-limits';
import { ManualQuoteComponentDto, ManualQuotePercentageDto } from 'korint-sdk';

export function getAdditionalQuoteComponentsFromFormPayload(formPayload: Record<string, unknown>) {
  return Object.values(formPayload).filter(
    (c): c is NonNullable<ManualQuoteComponentDto> =>
      !!c &&
      typeof c === 'object' &&
      'id' in c &&
      'type' in c &&
      'chargeType' in c &&
      'premium' in c &&
      typeof c.premium === 'object',
  );
}

export function getAdditionalQuotePercentagesFromFormPayload(formPayload: Record<string, unknown>) {
  return Object.values(formPayload).filter(
    (c): c is NonNullable<ManualQuotePercentageDto> =>
      !!c &&
      typeof c === 'object' &&
      'id' in c &&
      'type' in c &&
      'chargeType' in c &&
      'componentIds' in c &&
      'amountPropertiesFrom' in c &&
      'percentage' in c,
  );
}

dayjs.extend(minMaxPlugin);

// The generated AssetStartedAtLimitDto types both bounds as bare `object`, hence the local shape
type RelativeDuration = {
  years?: number;
  months?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  milliseconds?: number;
};

type StartedAtBound = { absolute?: string; relative?: RelativeDuration };

// Finest unit first — the backend snaps a boundary to the finest unit the duration carries
const durationUnits = [
  ['milliseconds', 'millisecond'],
  ['seconds', 'second'],
  ['minutes', 'minute'],
  ['hours', 'hour'],
  ['days', 'day'],
  ['months', 'month'],
  ['years', 'year'],
] as const;

// The datetime input serializes to the minute, so a bound carrying seconds must not loosen
const ceilToMinute = (value: Dayjs) =>
  value.isSame(value.startOf('minute')) ? value : value.startOf('minute').add(1, 'minute');

function readBound(bound: unknown): StartedAtBound {
  return typeof bound === 'object' && bound !== null ? (bound as StartedAtBound) : {};
}

function shift(from: Dayjs, relative: RelativeDuration, direction: 1 | -1) {
  return durationUnits.reduce((moment, [key, unit]) => {
    const amount = relative[key];
    return amount === undefined ? moment : moment.add(direction * amount, unit);
  }, from);
}

function finestUnit(relative: RelativeDuration) {
  return durationUnits.find(([key]) => relative[key] !== undefined)?.[1] ?? 'millisecond';
}

/**
 * Mirrors the backend's assetStartedAtLimit check (asset.utils.ts checkLimitDate): `min.relative` is
 * subtracted from now, so a negative duration there reads as a lead time.
 */
export function resolveAssetStartedAtBounds(
  limit?: { min?: unknown; max?: unknown },
  { ignoreRelativeMinimum = false }: { ignoreRelativeMinimum?: boolean } = {},
): {
  min?: string;
  max?: string;
} {
  if (!limit) return {};

  const now = dayjs();
  const minBound = readBound(limit.min);
  const maxBound = readBound(limit.max);

  const lowerBounds = [
    minBound.absolute ? ceilToMinute(dayjs(minBound.absolute)) : undefined,
    minBound.relative && !ignoreRelativeMinimum
      ? shift(now, minBound.relative, -1).startOf(finestUnit(minBound.relative))
      : undefined,
  ].filter((bound): bound is Dayjs => !!bound);

  const upperBounds = [
    maxBound.absolute ? dayjs(maxBound.absolute).startOf('minute') : undefined,
    maxBound.relative
      ? shift(now, maxBound.relative, 1).endOf(finestUnit(maxBound.relative))
      : undefined,
  ].filter((bound): bound is Dayjs => !!bound);

  return {
    min: lowerBounds.length ? dayjs.max(lowerBounds)?.toISOString() : undefined,
    max: upperBounds.length ? dayjs.min(upperBounds)?.toISOString() : undefined,
  };
}

// A field bound may be a token (`now`), so both sides go through the same parser as the resolver
const parsedBounds = (bounds: Array<string | undefined>) =>
  bounds
    .map(bound => parseDateLimit(bound))
    .filter((bound): bound is Dayjs => bound !== undefined && bound.isValid());

/** The stricter of two `min` bounds, so a section constraint is never loosened by the config. */
export function laterBound(a?: string, b?: string) {
  const bounds = parsedBounds([a, b]);
  if (bounds.length === 0) return a ?? b;

  return dayjs.max(bounds)?.toISOString();
}

/** The stricter of two `max` bounds. */
export function earlierBound(a?: string, b?: string) {
  const bounds = parsedBounds([a, b]);
  if (bounds.length === 0) return a ?? b;

  return dayjs.min(bounds)?.toISOString();
}
